import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useSession } from "../lib/session";

interface Pack {
  id: string;
  price_usd: number;
  base_totems: number;
  bonus_totems: number;
  label?: string;
}
interface BalanceResp {
  balance: number;
  anonymous?: boolean;
  packs: Pack[];
  recent?: Array<{
    id: string;
    delta: number;
    kind: string;
    created_at: number;
    note: string | null;
  }>;
}

async function fetchBalance(): Promise<BalanceResp> {
  const r = await fetch("/api/me/balance", { credentials: "include" });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
}

export default function Coins(): JSX.Element {
  const nav = useNavigate();
  const user = useSession((s) => s.user);
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["balance"], queryFn: fetchBalance });
  const [busyPack, setBusyPack] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function buy(packId: string): Promise<void> {
    setBusyPack(packId);
    setErr(null);
    try {
      const r = await fetch("/api/coins/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ pack_id: packId }),
      });
      const data = (await r.json()) as { ok?: boolean; mode?: string; checkout_url?: string; error?: string };
      if (!r.ok) throw new Error(data.error ?? `HTTP ${r.status}`);
      if (data.checkout_url) {
        // Real Stripe path — hand off to checkout
        window.location.href = data.checkout_url;
        return;
      }
      // Fake mode credits immediately. Refresh balance + nudge.
      await qc.invalidateQueries({ queryKey: ["balance"] });
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyPack(null);
    }
  }

  if (!user) {
    return (
      <section className="coins-wrap">
        <h1>Top up your totems</h1>
        <p>
          Buy prepaid totems once, then drop them on any streamer in one click. Bigger packs
          carry bigger bonuses.
        </p>
        <Link to="/login?return_to=/coins" className="btn btn-primary" style={{ marginTop: 12 }}>
          Sign in to buy
        </Link>
      </section>
    );
  }

  return (
    <section className="coins-wrap">
      <header className="coins-head">
        <h1>Top up your totems</h1>
        <div className="coins-balance">
          <span className="coins-balance-lbl">Your balance</span>
          <span className="coins-balance-val">
            ⚡ {q.isLoading ? "…" : (q.data?.balance ?? 0).toLocaleString()} <small>totems</small>
          </span>
        </div>
      </header>

      <p className="coins-blurb">
        1 totem = $1 of tip credit. Buy once, drop on any streamer in one click.
      </p>

      <div className="coins-grid">
        {(q.data?.packs ?? []).map((p) => {
          const total = p.base_totems + p.bonus_totems;
          const pricePerTotem = (p.price_usd / total).toFixed(2);
          return (
            <button
              key={p.id}
              type="button"
              className={`pack ${p.label ? "pack-feature" : ""}`}
              onClick={() => buy(p.id)}
              disabled={busyPack !== null}
            >
              {p.label ? <div className="pack-badge">{p.label}</div> : null}
              <div className="pack-totems">⚡ {total}</div>
              <div className="pack-bonus">
                {p.base_totems} {p.bonus_totems > 0 ? <span>+ {p.bonus_totems} bonus</span> : null}
              </div>
              <div className="pack-price">${p.price_usd}</div>
              <div className="pack-per">${pricePerTotem}/totem</div>
              <div className="pack-cta">{busyPack === p.id ? "Working…" : "Buy"}</div>
            </button>
          );
        })}
      </div>
      {err ? <p className="coins-err">{err}</p> : null}

      <div className="coins-recent">
        <h3>Recent activity</h3>
        {q.data?.recent && q.data.recent.length > 0 ? (
          <ul>
            {q.data.recent.map((r) => (
              <li key={r.id}>
                <span className={`kind kind-${r.kind}`}>{r.kind}</span>
                <span className={`delta ${r.delta >= 0 ? "pos" : "neg"}`}>
                  {r.delta >= 0 ? "+" : ""}{r.delta} ⚡
                </span>
                <span className="ago">{new Date(r.created_at).toLocaleString()}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ color: "var(--text-mute)" }}>No activity yet.</p>
        )}
      </div>

      <div style={{ marginTop: 24 }}>
        <button type="button" onClick={() => nav(-1)} className="btn">← Back</button>
      </div>

      <style>{`
        .coins-wrap { max-width: 880px; margin: 40px auto; padding: 0 20px; }
        .coins-head { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 12px; }
        .coins-head h1 { font-family: var(--f-display); font-size: 36px; font-weight: 800; margin: 0; }
        .coins-balance { text-align: right; }
        .coins-balance-lbl { font-family: var(--f-mono); font-size: 10px; letter-spacing: .14em; color: var(--text-mute); text-transform: uppercase; display: block; }
        .coins-balance-val { font-family: var(--f-mono); font-weight: 800; font-size: 24px; color: var(--gold, #FFD700); text-shadow: 0 0 12px var(--gold-deep, #b78d00); }
        .coins-balance-val small { font-size: 12px; color: var(--text-sec); font-weight: 600; }
        .coins-blurb { color: var(--text-sec); margin: 8px 0 28px; }
        .coins-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; }
        .pack {
          background: var(--bg-card); border: 1px solid var(--line); border-radius: 12px;
          padding: 20px 16px; cursor: pointer; text-align: center;
          color: var(--text-pri); position: relative;
          transition: transform 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
        }
        .pack:hover:not(:disabled) { transform: translateY(-2px); border-color: var(--bits, #9146FF); box-shadow: 0 12px 32px #9146FF22; }
        .pack:disabled { opacity: .6; cursor: not-allowed; }
        .pack-feature { border-color: var(--gold, #FFD700); box-shadow: 0 0 24px #FFD70033; }
        .pack-badge {
          position: absolute; top: -10px; left: 50%; transform: translateX(-50%);
          background: var(--gold, #FFD700); color: #1a1300; font-family: var(--f-mono); font-size: 10px;
          font-weight: 800; letter-spacing: .14em; text-transform: uppercase;
          padding: 4px 10px; border-radius: 999px;
        }
        .pack-totems { font-family: var(--f-display); font-weight: 800; font-size: 32px; color: var(--gold, #FFD700); line-height: 1; }
        .pack-bonus { font-family: var(--f-mono); font-size: 11px; color: var(--text-sec); margin-top: 4px; letter-spacing: .04em; }
        .pack-bonus span { color: #76FF03; font-weight: 800; }
        .pack-price { font-family: var(--f-display); font-weight: 800; font-size: 24px; margin-top: 14px; }
        .pack-per { font-family: var(--f-mono); font-size: 10px; color: var(--text-mute); margin-top: 2px; letter-spacing: .04em; }
        .pack-cta {
          margin-top: 14px; padding: 8px; border-radius: 6px;
          background: var(--bits, #9146FF); color: #fff;
          font-family: var(--f-mono); font-weight: 800; font-size: 12px; letter-spacing: .12em; text-transform: uppercase;
        }
        .coins-err { color: var(--danger, #ff5252); font-size: 13px; margin-top: 14px; }
        .coins-recent { margin-top: 36px; }
        .coins-recent h3 { font-family: var(--f-display); font-weight: 800; font-size: 18px; margin-bottom: 10px; }
        .coins-recent ul { list-style: none; padding: 0; margin: 0; border: 1px solid var(--line); border-radius: 8px; }
        .coins-recent li { display: grid; grid-template-columns: 80px 1fr auto; gap: 12px; padding: 10px 14px; border-bottom: 1px solid var(--line); align-items: center; font-size: 13px; }
        .coins-recent li:last-child { border-bottom: 0; }
        .kind { font-family: var(--f-mono); font-size: 10px; letter-spacing: .14em; text-transform: uppercase; color: var(--text-mute); }
        .kind-purchase { color: #76FF03; }
        .kind-donation { color: #FF1744; }
        .delta { font-family: var(--f-mono); font-weight: 800; font-size: 14px; }
        .delta.pos { color: #76FF03; }
        .delta.neg { color: #FF1744; }
        .ago { font-family: var(--f-mono); font-size: 11px; color: var(--text-mute); }
        @media (max-width: 720px) {
          .coins-grid { grid-template-columns: repeat(2, 1fr); }
        }
        @media (max-width: 380px) {
          .coins-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </section>
  );
}
